/**
 * Two people's answers, side by side.
 *
 * Until now nothing in this app ever showed one person another's slider
 * positions — only percentages. Two places now do, and both of them are
 * consented: a match, where the pair has already been introduced and is
 * deciding whether to swap contact details, and a public profile whose owner
 * has set a password and handed it to somebody.
 *
 * One property holds in both, and it is the whole reason this is safe enough
 * to build: **a row only exists where both people answered.** The comparison is
 * assembled from the same overlap traversal the score is, so there is nothing
 * here you could see without having answered the question yourself. You cannot
 * open somebody's profile, read their positions and then tune your own to
 * match — by the time you can see theirs, yours is already recorded.
 *
 * The ordering rule is the other half of the point. Sorted by distance
 * ascending, agreements land at the top and the places two people are pulling
 * in opposite directions collect at the bottom, which is the order somebody
 * deciding whether to meet actually wants to read them in: here's what we have
 * in common, and here is what you'd be signing up for.
 */

import { ladderLabel } from "./ladders";
import {
  pairWeight,
  similarity,
  type Importance,
  type ScaleValue,
} from "./scale";

/**
 * One question, answered by both.
 *
 * `their*` fields are null for the ordinary case of a question compared against
 * itself, and populated for a complementary pair — where their answer was given
 * to a *different* question, and saying so is the difference between "you two
 * disagree" and "you two fit".
 */
export interface AnswerComparison {
  questionId: string;
  prompt: string;
  help: string;
  ladder: string;
  /** The complementary question their answer belongs to, or null. */
  theirQuestionId: string | null;
  theirPrompt: string | null;
  theirLadder: string | null;
  yours: ScaleValue;
  theirs: ScaleValue;
  yourImportance: Importance;
  theirImportance: Importance;
  /** The pair's base weight — the mean of the two halves of a linked pair. */
  weight: number;
  groupKey: string;
  groupLabel: string;
  emoji: string;
  groupOrder: number;
}

// ---------------------------------------------------------------------------
// Bands
// ---------------------------------------------------------------------------

export type BandId = "same" | "near" | "apart" | "opposed";

/**
 * The four ways a pair of answers can read, cut where the similarity table in
 * `scale.ts` changes character rather than at even intervals:
 *
 *   0 stops   the same answer
 *   1 stop    two people reading one slider slightly differently
 *   2–3       a real difference, still worth something
 *   4+        the cliff — worth nothing to the score at all
 *
 * Deriving the cuts from the table by hand would be neater, but the *names* are
 * the point and there's no honest way to generate those, so the boundaries are
 * asserted against `SIMILARITY_BY_DISTANCE` in the tests instead.
 */
export interface Band {
  id: BandId;
  /** Shown on the row. */
  label: string;
  /** Heading for a run of rows in this band. */
  heading: string;
  /** Widest gap that still lands in this band. */
  maxDistance: number;
}

export const BANDS: readonly Band[] = [
  { id: "same", label: "Same answer", heading: "The same answer", maxDistance: 0 },
  { id: "near", label: "One apart", heading: "As near as makes no difference", maxDistance: 1 },
  { id: "apart", label: "Different", heading: "Different", maxDistance: 3 },
  { id: "opposed", label: "Opposed", heading: "Pulling opposite ways", maxDistance: 6 },
];

export function distanceOf(row: AnswerComparison): number {
  return Math.abs(row.yours - row.theirs);
}

export function bandOf(row: AnswerComparison): Band {
  const d = distanceOf(row);
  return BANDS.find((b) => d <= b.maxDistance) ?? BANDS[BANDS.length - 1]!;
}

/**
 * How much this row moved the score, 0..1 relative to the heaviest row given.
 *
 * A question both people shrugged at carries no weight however far apart the
 * two answers are, and showing it with the same emphasis as one they both
 * marked "very important" would misrepresent what the percentage was built
 * from. Relative rather than absolute because the absolute number is a product
 * of three multipliers and means nothing on its own.
 */
export function weightOf(row: AnswerComparison): number {
  return pairWeight({
    a: row.yours,
    b: row.theirs,
    importanceA: row.yourImportance,
    importanceB: row.theirImportance,
    weight: row.weight,
  });
}

export function similarityOf(row: AnswerComparison): number {
  return similarity(row.yours, row.theirs);
}

/** The phrase each side's slider position is worth, on its own question. */
export function labelsOf(row: AnswerComparison): { yours: string; theirs: string } {
  return {
    yours: ladderLabel(row.ladder, row.yours),
    theirs: ladderLabel(row.theirLadder ?? row.ladder, row.theirs),
  };
}

// ---------------------------------------------------------------------------
// The order
// ---------------------------------------------------------------------------

/**
 * Same answers first, opposed answers last.
 *
 * Distance decides it. Within one distance the heavier row goes first, so among
 * a screenful of identical answers the ones the two of them both called
 * important are the ones read first — and among the disagreements at the
 * bottom, the one that actually cost them is at the top of that run rather than
 * buried in the middle of it. The prompt breaks the remaining ties so the order
 * is stable across renders rather than left to the sort's implementation.
 */
export function byDifference(a: AnswerComparison, b: AnswerComparison): number {
  const d = distanceOf(a) - distanceOf(b);
  if (d !== 0) return d;
  const w = weightOf(b) - weightOf(a);
  if (w !== 0) return w;
  return a.prompt.localeCompare(b.prompt);
}

export function sortByDifference(rows: AnswerComparison[]): AnswerComparison[] {
  return [...rows].sort(byDifference);
}

// ---------------------------------------------------------------------------
// Shapes the views want
// ---------------------------------------------------------------------------

export interface ComparisonTally {
  total: number;
  same: number;
  near: number;
  apart: number;
  opposed: number;
}

export function tally(rows: AnswerComparison[]): ComparisonTally {
  const out: ComparisonTally = { total: rows.length, same: 0, near: 0, apart: 0, opposed: 0 };
  for (const row of rows) out[bandOf(row).id]++;
  return out;
}

export interface ComparedGroup {
  groupKey: string;
  groupLabel: string;
  emoji: string;
  rows: AnswerComparison[];
  tally: ComparisonTally;
}

/**
 * The rows split by area, each area's rows in difference order.
 *
 * Groups come back in catalogue order rather than sorted by agreement: these
 * line up beside the per-area score bars, which are already in that order, and
 * two lists of the same areas in different orders would be unreadable.
 */
export function byGroup(rows: AnswerComparison[]): ComparedGroup[] {
  const map = new Map<string, ComparedGroup & { order: number }>();

  for (const row of rows) {
    let entry = map.get(row.groupKey);
    if (!entry) {
      entry = {
        groupKey: row.groupKey,
        groupLabel: row.groupLabel,
        emoji: row.emoji,
        order: row.groupOrder,
        rows: [],
        tally: { total: 0, same: 0, near: 0, apart: 0, opposed: 0 },
      };
      map.set(row.groupKey, entry);
    }
    entry.rows.push(row);
  }

  return [...map.values()]
    .sort((a, b) => a.order - b.order || a.groupLabel.localeCompare(b.groupLabel))
    .map(({ order, ...g }) => ({ ...g, rows: sortByDifference(g.rows), tally: tally(g.rows) }));
}
